import {
  ArrowRight,
  Check,
  ChevronDown,
  Coins,
  CreditCard,
  History,
  Plus,
  ReceiptText,
  RefreshCw,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";
import { PageHeader } from "./PageHeader.jsx";
import { CopyableDate } from "./CopyableDate.jsx";
import { formatCredits } from "./credit-format.js";
import {
  billingPaymentStatusLabel,
  formatBillingPaymentDate,
  formatRenewalSummary,
} from "./billing-format.js";

const activityDate = new Intl.DateTimeFormat("es-AR", {
  weekday: "long",
  day: "numeric",
  month: "long",
});

function dateLabel(value) {
  const date = new Date(`${value}T12:00:00`);
  const today = new Date();
  const todayKey = [
    today.getFullYear(),
    String(today.getMonth() + 1).padStart(2, "0"),
    String(today.getDate()).padStart(2, "0"),
  ].join("-");
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const yesterdayKey = [
    yesterday.getFullYear(),
    String(yesterday.getMonth() + 1).padStart(2, "0"),
    String(yesterday.getDate()).padStart(2, "0"),
  ].join("-");
  if (value === todayKey) return "Hoy";
  if (value === yesterdayKey) return "Ayer";
  const formatted = activityDate.format(date);
  return formatted.charAt(0).toUpperCase() + formatted.slice(1);
}

function groupActivity(activity) {
  const groups = new Map();
  for (const entry of activity) {
    if (!groups.has(entry.date)) groups.set(entry.date, []);
    groups.get(entry.date).push(entry);
  }
  return [...groups.entries()];
}

function groupBreakdownByProduct(breakdown = []) {
  const products = new Map();
  for (const detail of breakdown) {
    const current = products.get(detail.productId) || {
      id: detail.productId,
      label: detail.productLabel,
      credits: 0,
      operations: [],
    };
    current.credits += detail.credits;
    current.operations.push(detail);
    products.set(detail.productId, current);
  }
  return [...products.values()].sort((left, right) => (
    right.credits - left.credits
    || left.label.localeCompare(right.label, "es")
  ));
}

function CreditActivityEntry({ date, entry }) {
  const [expanded, setExpanded] = useState(false);
  const products = groupBreakdownByProduct(entry.breakdown);

  if (!products.length) {
    return (
      <article>
        <span>{entry.label}</span>
        <strong>
          {formatCredits(entry.credits)}
          <small> créditos</small>
        </strong>
      </article>
    );
  }

  const detailId = `credit-detail-${date}-${entry.featureId}`;
  return (
    <article className="suite-credit-activity-entry--expandable">
      <button
        type="button"
        className="suite-credit-activity-row"
        aria-expanded={expanded}
        aria-controls={detailId}
        onClick={() => setExpanded((value) => !value)}
      >
        <span>
          <strong>{entry.label}</strong>
          <small>{expanded ? "Ocultar desglose" : "Ver desglose"}</small>
        </span>
        <span>
          <strong>{formatCredits(entry.credits)}<small> créditos</small></strong>
          <ChevronDown size={16} aria-hidden="true" />
        </span>
      </button>
      {expanded && (
        <div className="suite-credit-breakdown" id={detailId}>
          {products.map((product) => (
            <section key={product.id}>
              <header>
                <strong>{product.label}</strong>
                <span>{formatCredits(product.credits)} créditos</span>
              </header>
              <div>
                {product.operations.map((operation) => (
                  <p key={`${operation.productId}:${operation.operationId}`}>
                    <span>{operation.operationLabel}</span>
                    <strong>{formatCredits(operation.credits)}</strong>
                  </p>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </article>
  );
}

const activitySeries = {
  expediente_intelligence_access: "suite-credit-series--intelligence",
  document_editor: "suite-credit-series--documents",
  justiniano: "suite-credit-series--justiniano",
  document_processor: "suite-credit-series--documents",
  judicial_interest: "suite-credit-series--interest",
  library: "suite-credit-series--library",
  jurisprudence: "suite-credit-series--jurisprudence",
  other_ai: "suite-credit-series--other",
};

function activitySeriesClass(featureId) {
  return activitySeries[featureId]
    || activitySeries[String(featureId).split(":")[0]]
    || activitySeries.other_ai;
}

function localDateKey(date) {
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
  ].join("-");
}

function activityTimeline(activity, length = 30) {
  const entriesByDate = new Map(groupActivity(activity));
  const days = [];
  const cursor = new Date();
  cursor.setHours(12, 0, 0, 0);
  cursor.setDate(cursor.getDate() - (length - 1));
  for (let index = 0; index < length; index += 1) {
    const date = localDateKey(cursor);
    const entries = entriesByDate.get(date) || [];
    days.push({
      date,
      entries,
      total: entries.reduce((sum, entry) => sum + entry.credits, 0),
    });
    cursor.setDate(cursor.getDate() + 1);
  }
  return days;
}

function CreditActivityTimeline({ activity }) {
  const days = activityTimeline(activity);
  const daysWithUsage = days.filter(({ total }) => total > 0);
  const [selectedDate, setSelectedDate] = useState(
    daysWithUsage.at(-1)?.date || days.at(-1)?.date,
  );
  const maximum = Math.max(0, ...days.map(({ total }) => total));
  const selected = days.find(({ date }) => date === selectedDate) || days.at(-1);
  const series = [...new Map(
    activity.map((entry) => [entry.featureId, entry]),
  ).values()];

  return (
    <div className="suite-credit-timeline">
      <div className="suite-credit-timeline-legend" aria-label="Herramientas">
        {series.map((entry) => (
          <span key={entry.featureId}>
            <i className={activitySeriesClass(entry.featureId)} aria-hidden="true" />
            {entry.label}
          </span>
        ))}
      </div>
      <div
        className="suite-credit-timeline-plot"
        role="group"
        aria-label="Consumo diario de créditos durante los últimos 30 días"
      >
        {days.map((day, index) => (
          <button
            type="button"
            key={day.date}
            className={day.date === selected?.date ? "is-selected" : ""}
            aria-label={`${dateLabel(day.date)}: ${
              day.total
                ? `${formatCredits(day.total)} créditos`
                : "sin consumo"
            }`}
            aria-pressed={day.date === selected?.date}
            onClick={() => setSelectedDate(day.date)}
            onFocus={() => setSelectedDate(day.date)}
            onMouseEnter={() => setSelectedDate(day.date)}
          >
            <span className="suite-credit-timeline-track">
              <span
                className="suite-credit-timeline-stack"
                style={{ height: maximum ? `${(day.total / maximum) * 100}%` : "0%" }}
              >
                {day.entries.map((entry) => (
                  <span
                    key={entry.featureId}
                    className={activitySeriesClass(entry.featureId)}
                    style={{ flexGrow: entry.credits }}
                  />
                ))}
              </span>
            </span>
            <small>{index % 7 === 1 || index === days.length - 1 ? new Date(`${day.date}T12:00:00`).getDate() : ""}</small>
          </button>
        ))}
      </div>
      <div className="suite-credit-timeline-selection" aria-live="polite">
        <strong>
          <CopyableDate value={selected.date}>
            {dateLabel(selected.date)}
          </CopyableDate>
        </strong>
        {selected.entries.length ? (
          <span>
            {selected.entries.map((entry) => (
              <span key={entry.featureId}>
                {entry.label}: {formatCredits(entry.credits)}
              </span>
            ))}
          </span>
        ) : (
          <span>Sin consumo de créditos.</span>
        )}
      </div>
    </div>
  );
}

function formatCurrency(minorUnits, currency = "ARS") {
  return new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(Number(minorUnits || 0) / 100);
}

function validEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());
}

const billingStatusLabels = {
  pending: "Activación sin completar",
  authorized: "Activa",
  paused: "Pausada",
  canceled: "Cancelada",
};

export function CreditsSettingsPage({
  loadCredits,
  loadActivity,
  loadBilling,
  createCreditOrder,
  createSubscription,
  reconcileBillingReturn,
  cancelSubscription,
  changeSubscription,
}) {
  const [account, setAccount] = useState(null);
  const [activity, setActivity] = useState([]);
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activityLoading, setActivityLoading] = useState(true);
  const [error, setError] = useState("");
  const [activityError, setActivityError] = useState("");
  const [billingOpen, setBillingOpen] = useState(false);
  const [billingError, setBillingError] = useState("");
  const [selectedBillingKey, setSelectedBillingKey] = useState("");
  const [subscriptionPayerEmail, setSubscriptionPayerEmail] = useState("");
  const [customCredits, setCustomCredits] = useState(100);
  const [billingAction, setBillingAction] = useState("");
  const [billingNotice, setBillingNotice] = useState("");
  const [billingReturnState, setBillingReturnState] = useState("");
  const [billingReturnMessage, setBillingReturnMessage] = useState("");

  useEffect(() => {
    let active = true;
    let retryTimer;

    function billingReturnReference() {
      if (typeof window === "undefined") return "";
      const parameters = new URLSearchParams(window.location.search);
      if (!parameters.has("payment") && !parameters.has("subscription")) return "";
      return (parameters.get("billingReference") || "").split("?")[0];
    }

    function clearBillingReturnParameters() {
      if (typeof window === "undefined") return;
      const url = new URL(window.location.href);
      [
        "payment",
        "subscription",
        "billingReference",
        "collection_id",
        "collection_status",
        "payment_id",
        "status",
        "external_reference",
        "payment_type",
        "merchant_order_id",
        "preference_id",
        "site_id",
        "processing_mode",
        "merchant_account_id",
      ].forEach((key) => url.searchParams.delete(key));
      window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
    }

    async function waitForBillingReturn(reference) {
      if (!reference || !reconcileBillingReturn) return;
      setBillingReturnState("checking");
      setBillingReturnMessage("Estamos confirmando tu pago con Mercado Pago…");
      let lastError = null;
      for (let attempt = 0; attempt < 12 && active; attempt += 1) {
        try {
          const result = await reconcileBillingReturn(reference);
          lastError = null;
          if (result.successful) {
            setBillingReturnState("success");
            setBillingReturnMessage(
              result.kind === "subscription"
                ? `Suscripción activada y ${formatCredits(result.credits)} créditos acreditados.`
                : `${formatCredits(result.credits)} créditos acreditados correctamente.`,
            );
            clearBillingReturnParameters();
            return;
          }
          if (result.complete) {
            setBillingReturnState("error");
            setBillingReturnMessage(
              "Mercado Pago no confirmó el cobro. No se agregaron créditos.",
            );
            clearBillingReturnParameters();
            return;
          }
        } catch (requestError) {
          lastError = requestError;
        }
        await new Promise((resolve) => {
          retryTimer = window.setTimeout(resolve, 1_250);
        });
      }
      if (!active) return;
      setBillingReturnState("pending");
      setBillingReturnMessage(
        lastError?.message
          || "El pago sigue procesándose. El saldo se actualizará cuando Mercado Pago lo confirme.",
      );
    }

    async function initializePage() {
      setLoading(true);
      setError("");
      setActivityLoading(true);
      setActivityError("");
      await waitForBillingReturn(billingReturnReference());
      if (!active) return;
      const [accountResult, activityResult, billingResult] = await Promise.allSettled([
        loadCredits(),
        loadActivity(30),
        loadBilling(),
      ]);
      if (!active) return;
      if (accountResult.status === "fulfilled") {
        setAccount(accountResult.value);
      } else {
        setError(
          accountResult.reason?.message || "No pudimos cargar tus créditos.",
        );
      }
      if (activityResult.status === "fulfilled") {
        setActivity(activityResult.value);
      } else {
        setActivityError(
          activityResult.reason?.message || "No pudimos cargar la actividad.",
        );
      }
      if (billingResult.status === "fulfilled") {
        setBilling(billingResult.value);
        setCustomCredits(
          billingResult.value.catalog.customPurchase.minimumCredits,
        );
      } else {
        setError(
          billingResult.reason?.message
            || "No pudimos cargar las opciones de facturación.",
        );
      }
      setLoading(false);
      setActivityLoading(false);
    }

    initializePage();
    return () => {
      active = false;
      if (retryTimer) window.clearTimeout(retryTimer);
    };
  }, [loadCredits, loadActivity, loadBilling, reconcileBillingReturn]);

  async function startCheckout(key, operation) {
    if (billingAction) return;
    setBillingAction(key);
    setBillingError("");
    try {
      const result = await operation();
      if (!result.checkoutUrl) {
        throw new Error("Mercado Pago no devolvió una URL de pago.");
      }
      window.location.assign(result.checkoutUrl);
    } catch (requestError) {
      setBillingError(requestError.message || "No pudimos iniciar el pago.");
    } finally {
      setBillingAction("");
    }
  }

  async function cancelPlan() {
    if (billingAction) return;
    setBillingAction("cancel");
    setError("");
    try {
      const wasPending = billing?.subscription?.status === "pending";
      await cancelSubscription();
      setBilling((current) => ({
        ...current,
        subscription: null,
        hasActiveSubscription: false,
        canBuyOneTime: current.workspace.type === "personal",
      }));
      setBillingNotice(
        wasPending
          ? "La activación pendiente fue descartada. No se realizó ningún cobro."
          : "La suscripción fue cancelada. Tu saldo no se eliminó.",
      );
    } catch (requestError) {
      setError(requestError.message || "No pudimos cancelar la suscripción.");
    } finally {
      setBillingAction("");
    }
  }

  function continueSubscriptionActivation() {
    const checkoutUrl = billing?.subscription?.checkoutUrl;
    if (!checkoutUrl || billingAction) return;
    window.location.assign(checkoutUrl);
  }

  async function changePlan(productKey) {
    if (billingAction) return;
    setBillingAction(productKey);
    setBillingError("");
    try {
      const subscription = await changeSubscription({ productKey });
      setBilling((current) => ({ ...current, subscription }));
      setBillingNotice(
        subscription.scheduledChange
          ? "El cambio quedó programado para la próxima renovación."
          : "Se mantendrá el plan actual.",
      );
      setBillingOpen(false);
    } catch (requestError) {
      setBillingError(requestError.message || "No pudimos cambiar el plan.");
    } finally {
      setBillingAction("");
    }
  }

  const subscriptionItems = billing?.catalog.items.filter(
    ({ kind, active }) => active && ["subscription", "contact"].includes(kind),
  ) || [];
  const oneTimeItems = billing?.catalog.items.filter(
    ({ kind, active }) => active && kind === "one_time",
  ) || [];
  const customPurchase = billing?.catalog.customPurchase;
  const customCreditsValid = Boolean(
    customPurchase
    && Number.isInteger(customCredits)
    && customCredits >= customPurchase.minimumCredits
    && customCredits <= customPurchase.maximumCredits
    && customCredits % customPurchase.stepCredits === 0
  );
  const selectedBillingItem = selectedBillingKey === "one_time_custom"
    ? customCreditsValid
      ? {
          key: "one_time_custom",
          kind: "one_time",
          credits: customCredits,
          priceMinorUnits: (customCredits / customPurchase.stepCredits)
            * customPurchase.pricePerStepMinorUnits,
          currency: customPurchase.currency,
        }
      : null
    : billing?.catalog.items.find(({ key }) => key === selectedBillingKey) || null;
  const changingPlan = Boolean(
    selectedBillingItem?.kind === "subscription"
    && billing?.subscription?.status === "authorized"
  );

  function openBillingDialog() {
    setError("");
    setBillingError("");
    setSelectedBillingKey("");
    setSubscriptionPayerEmail("");
    setBillingOpen(true);
  }

  function closeBillingDialog() {
    if (billingAction) return;
    setBillingError("");
    setBillingOpen(false);
  }

  async function confirmBillingSelection() {
    if (!selectedBillingItem || billingAction) return;
    if (selectedBillingItem.kind === "subscription") {
      if (changingPlan) {
        await changePlan(selectedBillingItem.key);
        return;
      }
      await startCheckout(
        selectedBillingItem.key,
        () => createSubscription({
          productKey: selectedBillingItem.key,
          payerEmail: subscriptionPayerEmail.trim(),
        }),
      );
      return;
    }
    await startCheckout(
      selectedBillingItem.key,
      () => createCreditOrder({
        productKey: selectedBillingItem.key,
        ...(selectedBillingItem.key === "one_time_custom"
          ? { credits: customCredits }
          : {}),
      }),
    );
  }

  return (
    <div className="suite-credits-page">
      <PageHeader
        hero
        eyebrow="Cuenta · Configuración"
        title="Créditos"
        description={account?.scope === "organization"
          ? "Saldo compartido por todas las personas que trabajan dentro de la organización."
          : "Una sola unidad para usar las funciones de inteligencia artificial en todos los productos de Tribunalea."}
        actions={billing?.workspace.canManage ? (
          <button
            type="button"
            className="suite-button suite-button--primary"
            disabled={loading || !account || !billing}
            onClick={openBillingDialog}
          >
            <Plus size={18} />
            Comprar créditos
          </button>
        ) : null}
      />

      {billingReturnMessage ? (
        <div
          className={`suite-billing-return-status is-${billingReturnState}`}
          role={billingReturnState === "error" ? "alert" : "status"}
        >
          {billingReturnState === "checking" ? (
            <RefreshCw className="suite-spin" size={18} aria-hidden="true" />
          ) : billingReturnState === "success" ? (
            <Check size={18} aria-hidden="true" />
          ) : null}
          <span>{billingReturnMessage}</span>
        </div>
      ) : null}

      {error && <div className="suite-credits-error">{error}</div>}

      {loading ? (
        <div className="suite-credits-loading">Cargando tus créditos…</div>
      ) : account ? (
        <>
          <section className="suite-credit-balance-card">
            <span className="suite-credit-balance-icon"><Coins size={28} /></span>
            <div>
              <span>Créditos disponibles</span>
              <strong>{formatCredits(account.balance)}</strong>
              <small>
                {account.scope === "organization"
                  ? "Este saldo pertenece a la organización activa."
                  : "Tu saldo se comparte entre todas las herramientas de IA."}
              </small>
            </div>
          </section>

          <section className="suite-credit-summary" aria-label="Resumen de créditos">
            <article>
              <span>Cargados</span>
              <strong>{formatCredits(account.granted)}</strong>
              <small>
                {account.scope === "personal"
                  ? <>La bonificación inicial vigente es de {formatCredits(account.defaultGrant)} créditos.</>
                  : account.monthlyIncluded
                    ? `La asignación mensual es de ${formatCredits(account.monthlyIncluded)} créditos.`
                    : "Se acreditan con cada pago aprobado."}
              </small>
            </article>
            <article>
              <span>Utilizados</span>
              <strong>{formatCredits(account.used)}</strong>
              <small>Consumo acumulado de las funciones de IA.</small>
            </article>
          </section>

          {billing ? (
            <section className="suite-billing-summary">
              <div className="suite-billing-summary-heading">
                <span className="suite-credit-activity-icon"><CreditCard size={20} /></span>
                <div>
                  <h2>Plan y facturación</h2>
                  <p>
                    {billing.workspace.type === "organization"
                      ? "La organización necesita una suscripción activa; las compras únicas son recargas adicionales."
                      : "Podés suscribirte para obtener un mejor precio o hacer una compra única."}
                  </p>
                </div>
              </div>
              <div className="suite-billing-current">
                {billing.subscription ? (
                  <>
                    <span>
                      <strong>{formatCredits(billing.subscription.creditsPerPeriod)} créditos/mes</strong>
                      <small>
                        {formatCurrency(
                          billing.subscription.amountMinorUnits,
                          billing.subscription.currency,
                        )} · {billingStatusLabels[billing.subscription.status] || billing.subscription.status}
                      </small>
                      {billing.subscription.status === "authorized"
                        && billing.subscription.nextPaymentAt ? (
                          <small className="suite-billing-renewal">
                            {formatRenewalSummary(billing.subscription.nextPaymentAt)}
                          </small>
                        ) : null}
                      {billing.subscription.scheduledChange ? (
                        <small className="suite-billing-scheduled-change">
                          Cambio programado: {formatCredits(
                            billing.subscription.scheduledChange.creditsPerPeriod,
                          )} créditos/mes por {formatCurrency(
                            billing.subscription.scheduledChange.amountMinorUnits,
                            billing.subscription.scheduledChange.currency,
                          )}. Se aplicará en la próxima renovación.
                        </small>
                      ) : null}
                    </span>
                    {billing.workspace.canManage
                      && ["pending", "authorized", "paused"].includes(billing.subscription.status) ? (
                        <div className="suite-billing-actions">
                          {billing.subscription.status === "pending"
                            && billing.subscription.checkoutUrl ? (
                              <button
                                type="button"
                                className="suite-button suite-button--primary"
                                disabled={Boolean(billingAction)}
                                onClick={continueSubscriptionActivation}
                              >
                                Continuar activación
                              </button>
                            ) : null}
                          <button
                            type="button"
                            className="suite-button suite-button--secondary"
                            disabled={Boolean(billingAction)}
                            onClick={cancelPlan}
                          >
                            {billingAction === "cancel"
                              ? "Cancelando…"
                              : billing.subscription.status === "pending"
                                ? "Descartar activación"
                                : "Cancelar suscripción"}
                          </button>
                        </div>
                      ) : null}
                  </>
                ) : (
                  <span>
                    <strong>Sin suscripción</strong>
                    <small>
                      {billing.workspace.type === "organization"
                        ? "Suscribite para habilitar el uso de los créditos del equipo."
                        : "Tu cuenta puede seguir usando compras únicas."}
                    </small>
                  </span>
                )}
              </div>
              {billingNotice ? <p className="suite-billing-notice">{billingNotice}</p> : null}
            </section>
          ) : null}

          {billing ? (
            <section className="suite-payment-history">
              <div className="suite-payment-history-heading">
                <span className="suite-credit-activity-icon"><ReceiptText size={20} /></span>
                <div>
                  <h2>Compras y pagos</h2>
                  <p>Últimos pagos de suscripciones y compras únicas.</p>
                </div>
              </div>
              {billing.payments?.length ? (
                <div className="suite-payment-history-list">
                  {billing.payments.map((payment) => {
                    const formattedDate = formatBillingPaymentDate(payment.createdAt);
                    return (
                      <article key={payment.id}>
                        <span className="suite-payment-history-main">
                          <strong>
                            {payment.kind === "subscription" ? "Suscripción mensual" : "Compra única"}
                          </strong>
                          {formattedDate ? (
                            <time dateTime={payment.createdAt}>{formattedDate}</time>
                          ) : null}
                        </span>
                        <span className="suite-payment-history-details">
                          <span>{formatCredits(payment.credits)} créditos</span>
                          <strong>{formatCurrency(payment.amountMinorUnits, payment.currency)}</strong>
                        </span>
                        <span className={`suite-payment-status is-${payment.status}`}>
                          {billingPaymentStatusLabel(payment.status)}
                        </span>
                      </article>
                    );
                  })}
                </div>
              ) : (
                <p className="suite-payment-history-empty">Todavía no hay compras ni pagos para mostrar.</p>
              )}
            </section>
          ) : null}

          <section className="suite-credit-activity">
            <div className="suite-credit-activity-heading">
              <span className="suite-credit-activity-icon"><History size={20} /></span>
              <div>
                <h2>Cómo usaste tus créditos</h2>
                <p>Consumo de los últimos 30 días, agrupado por día y herramienta.</p>
              </div>
            </div>
            {activityLoading ? (
              <p className="suite-credit-activity-empty">Cargando actividad…</p>
            ) : activityError ? (
              <p className="suite-credit-activity-error">{activityError}</p>
            ) : activity.length ? (
              <>
                <CreditActivityTimeline activity={activity} />
                <div className="suite-credit-activity-days">
                  {groupActivity(activity).map(([date, entries]) => (
                    <section className="suite-credit-activity-day" key={date}>
                      <h3><CopyableDate value={date}>{dateLabel(date)}</CopyableDate></h3>
                      <div>
                        {entries.map((entry) => (
                          <CreditActivityEntry
                            key={`${date}:${entry.featureId}`}
                            date={date}
                            entry={entry}
                          />
                        ))}
                      </div>
                    </section>
                  ))}
                </div>
              </>
            ) : (
              <p className="suite-credit-activity-empty">Todavía no usaste créditos en los últimos 30 días.</p>
            )}
          </section>
        </>
      ) : null}

      {billingOpen && billing?.workspace.canManage ? (
        <div
          className="suite-modal-backdrop"
          role="presentation"
          onMouseDown={closeBillingDialog}
        >
          <div
            className="suite-dialog suite-credit-dialog suite-billing-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="suite-add-credits-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="suite-icon-button suite-dialog-close"
              aria-label="Cerrar"
              disabled={Boolean(billingAction)}
              onClick={closeBillingDialog}
            ><X size={19} /></button>
            <span className="suite-dialog-icon suite-credit-dialog-icon"><Coins size={22} /></span>
            <h2 id="suite-add-credits-title">Comprar créditos</h2>
            <p>Elegí una suscripción mensual o una recarga única.</p>
            {billingError ? (
              <div className="suite-billing-dialog-error" role="alert">
                {billingError}
              </div>
            ) : null}

            {!billing.subscription
              || ["authorized", "canceled"].includes(billing.subscription.status) ? (
              <section className="suite-billing-options-section">
                <div className="suite-billing-section-title">
                  <RefreshCw size={17} />
                  <div><strong>Suscripción mensual</strong><small>Mejor precio por crédito. El saldo se acumula.</small></div>
                </div>
                <div className="suite-credit-options suite-credit-options--billing suite-credit-options--subscriptions">
                  {subscriptionItems.map((item) => item.kind === "contact" ? (
                    <a
                      className="suite-credit-option"
                      key={item.key}
                      href="mailto:contacto@tribunalea.com?subject=Plan%20personalizado%20Tribunalea"
                    >
                      <strong>Personalizado</strong><span>Contactanos</span>
                    </a>
                  ) : (
                    <button
                      type="button"
                      key={item.key}
                      className={selectedBillingKey === item.key ? "is-selected" : ""}
                      aria-pressed={selectedBillingKey === item.key}
                      disabled={Boolean(billingAction)
                        || billing.subscription?.scheduledChange?.productKey === item.key
                        || (
                          billing.subscription?.productKey === item.key
                          && !billing.subscription?.scheduledChange
                        )}
                      onClick={() => {
                        setSelectedBillingKey(item.key);
                        setBillingError("");
                      }}
                    >
                      <strong>{formatCredits(item.credits)}</strong>
                      <span>créditos/mes</span>
                      <em>{formatCurrency(item.priceMinorUnits, item.currency)}</em>
                      {billing.subscription?.scheduledChange?.productKey === item.key
                        ? <em>Próximo plan</em>
                        : billing.subscription?.productKey === item.key
                          ? <em>{billing.subscription?.scheduledChange
                            ? "Mantener plan actual"
                            : "Plan actual"}</em>
                        : selectedBillingKey === item.key
                          ? <Check size={16} />
                          : <ArrowRight size={16} />}
                    </button>
                  ))}
                </div>
              </section>
            ) : null}

            <section className="suite-billing-options-section">
              <div className="suite-billing-section-title">
                <Coins size={17} />
                <div><strong>Compra única</strong><small>Créditos adicionales sin vencimiento.</small></div>
              </div>
              {!billing.canBuyOneTime ? (
                <p className="suite-billing-requirement">
                  Activá primero una suscripción para comprar recargas para la organización.
                </p>
              ) : (
                <>
                  <div className="suite-credit-options suite-credit-options--billing suite-credit-options--one-time">
                    {oneTimeItems.map((item) => (
                      <button
                        type="button"
                        key={item.key}
                        className={selectedBillingKey === item.key ? "is-selected" : ""}
                        aria-pressed={selectedBillingKey === item.key}
                        disabled={Boolean(billingAction)}
                        onClick={() => {
                          setSelectedBillingKey(item.key);
                          setBillingError("");
                        }}
                      >
                        <strong>{formatCredits(item.credits)}</strong>
                        <span>créditos</span>
                        <em>{formatCurrency(item.priceMinorUnits, item.currency)}</em>
                        {selectedBillingKey === item.key
                          ? <Check size={16} />
                          : <ArrowRight size={16} />}
                      </button>
                    ))}
                  </div>
                  {customPurchase?.enabled ? (
                    <div className="suite-custom-credit-purchase">
                      <label>
                        Cantidad personalizada
                        <input
                          type="number"
                          min={customPurchase.minimumCredits}
                          max={customPurchase.maximumCredits}
                          step={customPurchase.stepCredits}
                          value={customCredits}
                          onChange={(event) => {
                            setCustomCredits(Number(event.target.value));
                            setBillingError("");
                          }}
                        />
                      </label>
                      <span>
                        {formatCurrency(
                          (customCredits / customPurchase.stepCredits)
                            * customPurchase.pricePerStepMinorUnits,
                          customPurchase.currency,
                        )}
                      </span>
                      <button
                        type="button"
                        className={`suite-button ${selectedBillingKey === "one_time_custom"
                          ? "suite-button--primary"
                          : "suite-button--secondary"}`}
                        disabled={Boolean(billingAction) || !customCreditsValid}
                        onClick={() => {
                          setSelectedBillingKey("one_time_custom");
                          setBillingError("");
                        }}
                      >
                        {selectedBillingKey === "one_time_custom" ? "Elegido" : "Elegir"}
                      </button>
                    </div>
                  ) : null}
                </>
              )}
            </section>

            {selectedBillingItem?.kind === "subscription" && !changingPlan ? (
              <div className="suite-subscription-payer">
                <label htmlFor="suite-subscription-payer-email">
                  Email de la cuenta de Mercado Pago
                  <input
                    id="suite-subscription-payer-email"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    placeholder="tu-email@ejemplo.com"
                    value={subscriptionPayerEmail}
                    disabled={Boolean(billingAction)}
                    aria-describedby="suite-subscription-payer-help"
                    onChange={(event) => {
                      setSubscriptionPayerEmail(event.target.value);
                      setBillingError("");
                    }}
                  />
                </label>
                <small id="suite-subscription-payer-help">
                  Puede ser distinto al email de Tribunalea. Mercado Pago lo necesita para identificar la suscripción.
                </small>
              </div>
            ) : null}

            <div className="suite-billing-confirmation">
              <span>
                <small>{selectedBillingItem ? "Tu selección" : "Siguiente paso"}</small>
                <strong>
                  {selectedBillingItem
                    ? `${formatCredits(selectedBillingItem.credits)} créditos${selectedBillingItem.kind === "subscription" ? "/mes" : ""} · ${formatCurrency(selectedBillingItem.priceMinorUnits, selectedBillingItem.currency)}`
                    : "Elegí una opción para continuar"}
                </strong>
              </span>
              <button
                type="button"
                className="suite-button suite-button--primary"
                disabled={!selectedBillingItem
                  || Boolean(billingAction)
                  || (
                    selectedBillingItem.kind === "subscription"
                    && !changingPlan
                    && !validEmail(subscriptionPayerEmail)
                  )}
                onClick={confirmBillingSelection}
              >
                {billingAction
                  ? "Procesando…"
                  : changingPlan
                    ? "Confirmar cambio"
                    : "Continuar a Mercado Pago"}
              </button>
            </div>
            <small className="suite-credit-preview-note">
              Los créditos se acreditan únicamente cuando Mercado Pago confirma el cobro.
            </small>
            <div className="suite-dialog-actions">
              <button
                type="button"
                className="suite-button suite-button--secondary"
                disabled={Boolean(billingAction)}
                onClick={closeBillingDialog}
              >Cerrar</button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
